import { type FC } from 'react';
import {
  Button,
  Flex,
  Heading,
  List,
  ListItem,
  LogoBox,
  Modal,
  Spinner,
  Text,
} from '@highereducation/hen-ui';
import type { Result } from '../../types/result';

export type DuplicateSubmissionPopupProps = {
  result: Result | null;
  otherPrograms: Result[];
  isLoading?: boolean;
  onClose: () => void;
  onViewNextSteps: () => void;
};

/**
 * Popup shown when a user re-submits a school they've already
 * requested info from. Body is composed entirely from hen-ui
 * primitives (Heading / Text / Button / List / Flex / LogoBox /
 * Spinner) inside the existing hen-ui `Modal` chrome -- no Figma
 * was provided for this surface, so the layout follows the
 * platform's primary modal conventions (centered content, max-h
 * scrolling, primary CTA).
 */
const DuplicateSubmissionPopup: FC<DuplicateSubmissionPopupProps> = ({
  result,
  otherPrograms,
  isLoading = false,
  onClose,
  onViewNextSteps,
}) => {
  if (!result) {
    return null;
  }

  const handleCtaClick = () => {
    onViewNextSteps();
  };

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Modal.Content size='md' className='overflow-y-auto'>
        <Modal.Header className='sr-only'>
          <Modal.Title>You already requested info from this school</Modal.Title>
        </Modal.Header>

        <Flex align='center' gap='lg' className='bg-primary-100 px-lg py-2xl'>
          {result.imageURL && (
            <LogoBox src={result.imageURL} alt={result.schoolName} />
          )}

          <Flex asChild gap='xs'>
            <section>
              <Heading variant='h3' align='center'>
                You&rsquo;ve already requested info from this school.
              </Heading>
              <Text variant='b4' align='center'>
                You requested information about:
              </Text>
              <Text variant='b4' weight='bold' color='heading' align='center'>
                {result.programName}
              </Text>
            </section>
          </Flex>

          <Flex asChild gap='xxs'>
            <section>
              <Text variant='b4' weight='bold' color='heading' align='center'>
                {result.schoolName}
              </Text>
              <Text variant='b4' align='center'>
                has many programs you might be interested in!
              </Text>
              <Text variant='b4' align='center'>
                Chat through your options with your advisors.
              </Text>
            </section>
          </Flex>

          <Flex
            asChild
            width='full'
            gap='sm'
            className='rounded-sm bg-white px-sm py-lg'
          >
            <section>
              <Heading variant='h6'>Other programs available:</Heading>

              {isLoading ? (
                <Flex direction='row' align='center' gap='xs'>
                  <Spinner label='Loading more programs' />
                  <Text variant='b4' color='subtext'>
                    Searching for more programs...
                  </Text>
                </Flex>
              ) : otherPrograms.length > 0 ? (
                <List bulletStyle='check' className='max-h-40 overflow-y-auto'>
                  {otherPrograms.map((program, index) => (
                    <ListItem key={`${program.programId}-${index}`}>
                      <Text variant='b4'>{program.programName}</Text>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Text variant='b4' color='subtext'>
                  We&rsquo;ll share more options soon.
                </Text>
              )}
            </section>
          </Flex>

          <Button
            type='button'
            variant='primaryFill'
            size='lg'
            className='w-full'
            onClick={handleCtaClick}
          >
            View next steps
          </Button>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};

export default DuplicateSubmissionPopup;
